/**
 * The numbers that exist only for some ads, or only split one way (notes/AUDIENCE.md 7.5): what people did on a
 * boosted post, each card of a swipe ad, and where the ads showed. Read from Meta's own rows as they came.
 */

type Row = Record<string, unknown>;
type Action = { action_type?: string; value?: string } & Record<string, unknown>;

const num = (v: unknown): number | null => {
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};
const listOf = (row: Row | null | undefined, field = "actions"): Action[] => (Array.isArray(row?.[field]) ? (row[field] as Action[]) : []);
const action = (row: Row | null | undefined, type: string) => {
  const hit = listOf(row).find((a) => a.action_type === type);
  return hit ? num(hit.value) : null;
};

/** Meta's own "Results": a list of indicators, each with its values. */
function resultsOf(row: Row): number | null {
  const v = row.results;
  if (!Array.isArray(v)) return null;
  return (v as { values?: { value?: string }[]; value?: string }[]).reduce((n, r) => n + (num(Array.isArray(r.values) ? r.values[0]?.value : r.value) ?? 0), 0);
}

// --- A post ---------------------------------------------------------------------------------------------

export interface PostNumbers {
  reactions: number | null;
  comments: number | null;
  shares: number | null;
  saves: number | null;
  profileVisits: number | null;
}

/** What people did on a boosted post, by Meta's action names (`post` is a share; `like` would be a Page like). */
export function postNumbers(row: Row | null): PostNumbers {
  return {
    reactions: action(row, "post_reaction"),
    comments: action(row, "comment"),
    shares: action(row, "post"),
    saves: action(row, "onsite_conversion.post_save"),
    profileVisits: num(row?.instagram_profile_visits),
  };
}

// --- Each card of a swipe ad ------------------------------------------------------------------------------

export interface CardNumbers {
  name: string;
  taps: number | null;
  /** Meta's click rate for the card, in percent as Meta sends it. */
  ctr: number | null;
}

const cardKey = (a: Action) => String(a.action_carousel_card_name ?? a.action_carousel_card_id ?? "");

/**
 * Each card's taps as Meta splits them, in the ad's own card order. Meta names a card by its headline, so a
 * card is matched by name; when no name matches and the counts agree, Meta's order is the ad's order.
 */
export function cardNumbers(rows: Row[] | null, platformAdId: string, cards: string[]): CardNumbers[] | null {
  const mine = (rows ?? []).filter((r) => String(r.ad_id ?? "") === platformAdId);
  const taps = new Map<string, number>();
  const ctr = new Map<string, number>();
  for (const r of mine) {
    for (const a of listOf(r)) if (a.action_type === "link_click" && cardKey(a)) taps.set(cardKey(a), (taps.get(cardKey(a)) ?? 0) + (num(a.value) ?? 0));
    for (const a of listOf(r, "website_ctr")) if (cardKey(a) && num(a.value) !== null) ctr.set(cardKey(a), num(a.value)!);
  }
  const keys = [...new Set([...taps.keys(), ...ctr.keys()])];
  if (!keys.length) return null;
  const byName = new Map(keys.map((k) => [k.trim().toLowerCase(), k]));
  const matched = cards.map((c) => byName.get(c.trim().toLowerCase()) ?? null);
  const order = matched.some(Boolean) ? matched : keys.length === cards.length ? keys : cards.map(() => null);
  const out = cards.map((name, i) => ({ name, taps: order[i] ? (taps.get(order[i]!) ?? 0) : null, ctr: order[i] ? (ctr.get(order[i]!) ?? null) : null }));
  // Why: a card Meta names that matches none of the ad's own (renamed since) still shows, under Meta's name.
  for (const k of keys) if (!order.includes(k)) out.push({ name: k, taps: taps.get(k) ?? 0, ctr: ctr.get(k) ?? null });
  return out;
}

// --- Where the ads showed ---------------------------------------------------------------------------------

export interface PlaceNumbers {
  key: string;
  name: string;
  spend: number;
  results: number | null;
  costEach: number | null;
  /** This spot's part of the money, 0 to 1. */
  share: number;
}

const APP: Record<string, string> = { facebook: "Facebook", instagram: "Instagram", messenger: "Messenger", audience_network: "Audience Network", threads: "Threads", whatsapp: "WhatsApp" };
const SPOT: Record<string, string> = {
  feed: "Feed",
  story: "Stories",
  facebook_stories: "Stories",
  instagram_stories: "Stories",
  reels: "Reels",
  facebook_reels: "Reels",
  instagram_reels: "Reels",
  facebook_reels_overlay: "Reels overlay",
  marketplace: "Marketplace",
  search: "Search results",
  instagram_search: "Search results",
  instream_video: "In-stream video",
  right_hand_column: "Right column",
  profile_feed: "Profile feed",
  instagram_profile_feed: "Profile feed",
  an_classic: "Apps and sites",
  rewarded_video: "Rewarded video",
  threads_feed: "Feed",
  status: "Status",
};

/** A spot's plain name from Meta's two values; a value Flashadz does not know yet shows in Meta's words. */
export function placeName(platform: string, position: string): string {
  const app = APP[platform] ?? platform.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
  const spot = SPOT[position] ?? position.replace(/^(facebook|instagram)_/, "").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
  return platform === "audience_network" && position === "an_classic" ? app : `${app} ${spot}`;
}

/** Where the ads showed, one line per spot with its spend and results, most money first; one ad's when an id is given. */
export function placeNumbers(rows: Row[] | null, platformAdId?: string): PlaceNumbers[] {
  const mine = (rows ?? []).filter((r) => !platformAdId || String(r.ad_id ?? "") === platformAdId);
  const spots = new Map<string, { name: string; spend: number; results: number | null }>();
  for (const r of mine) {
    const platform = String(r.publisher_platform ?? "");
    const position = String(r.platform_position ?? "");
    if (!platform) continue;
    const key = `${platform}:${position}`;
    const s = spots.get(key) ?? { name: placeName(platform, position), spend: 0, results: null };
    s.spend += num(r.spend) ?? 0;
    const res = resultsOf(r);
    if (res !== null) s.results = (s.results ?? 0) + res;
    spots.set(key, s);
  }
  const total = [...spots.values()].reduce((t, s) => t + s.spend, 0);
  return [...spots.entries()]
    .map(([key, s]) => ({ key, name: s.name, spend: s.spend, results: s.results, costEach: s.results ? s.spend / s.results : null, share: total ? s.spend / total : 0 }))
    .toSorted((a, b) => b.spend - a.spend);
}
